import React, {useMemo, useState} from "react";
import {BotType, LocaleTag} from "../../../../../../../../api/generated";
import {useAppDispatch, useAppSelector} from "../../../../../../../../app/hooks";
import {SelectDropdown} from "../../../../../../../elements/select/SelectDropdown";
import classnames from "classnames";
import {
    addLocaleWithDefaults,
    removeConfiguredLocale,
    selectConfiguredLocales,
    selectDefaultLocale,
    selectSelectedLocale,
    setSelectedLocale
} from "../../../widgetAppearanceUpdateSlice";
import {
    getWidgetLocaleLabel,
    widgetLocaleOptions
} from "../../../widgetLocaleOptions";
import {
    AppearanceTabSettingsContainer
} from "../../../../../../../elements/appearanceTabSettingsContainer/AppearanceTabSettingsContainer";
import styles from "../WidgetAppearanceForm.module.css";

interface LocaleSettingsProps {
    botType: BotType;
}

export const LocaleSettings: React.FC<LocaleSettingsProps> = ({botType}) => {
    const dispatch = useAppDispatch();

    const defaultLocale = useAppSelector(selectDefaultLocale);
    const selectedLocale = useAppSelector(selectSelectedLocale);
    const configuredLocales = useAppSelector(selectConfiguredLocales);
    const isDefaultLocaleSelected = selectedLocale === defaultLocale;

    const addableLocales = useMemo(
        () => widgetLocaleOptions.filter(option => !configuredLocales.includes(option.value)),
        [configuredLocales]
    );

    const [localeToAdd, setLocaleToAdd] = useState<LocaleTag | undefined>(addableLocales[0]?.value);

    React.useEffect(() => {
        if (!localeToAdd || !addableLocales.some(option => option.value === localeToAdd)) {
            setLocaleToAdd(addableLocales[0]?.value);
        }
    }, [addableLocales, localeToAdd]);

    const configuredLocaleOptions = configuredLocales
        .map(locale => ({label: getWidgetLocaleLabel(locale), value: locale}))
        .sort((left, right) => {
            const leftIsDefault = left.value === defaultLocale;
            const rightIsDefault = right.value === defaultLocale;

            if (leftIsDefault !== rightIsDefault) {
                return leftIsDefault ? -1 : 1;
            }

            return left.label.localeCompare(right.label);
        });

    const onAddLocale = () => {
        if (!localeToAdd) {
            return;
        }
        dispatch(addLocaleWithDefaults({botType, locale: localeToAdd}));
    };

    return (
        <AppearanceTabSettingsContainer title="Locale Translations">
            <div className={styles.localeChipsRow}>
                {configuredLocaleOptions.map((option) => {
                    const isSelected = option.value === selectedLocale;
                    const isDefault = option.value === defaultLocale;

                    return (
                        <div key={option.value} className={styles.localeChipContainer}>
                            <button
                                type="button"
                                className={classnames(styles.localeChip, isSelected && styles.localeChipSelected)}
                                onClick={() => dispatch(setSelectedLocale(option.value))}
                            >
                                <span>{option.label}</span>
                                {isDefault && <span className={styles.localeChipBadge}>Default</span>}
                            </button>
                            {!isDefault && (
                                <button
                                    type="button"
                                    className={styles.localeChipRemoveButton}
                                    aria-label={`Remove ${option.label}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        dispatch(removeConfiguredLocale(option.value));
                                    }}
                                >
                                    -
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {addableLocales.length > 0 && (
                <div className={styles.localeAddRow}>
                    <div className={styles.localeAddSelect}>
                        <SelectDropdown<LocaleTag>
                            title="Add Locale"
                            value={localeToAdd}
                            handleChange={(locale) => setLocaleToAdd(locale)}
                            options={addableLocales}
                            disabled={false}
                            selectFirstOptionByDefault={false}
                        />
                    </div>
                    <button
                        type="button"
                        className={styles.localeAddIconButton}
                        onClick={onAddLocale}
                        aria-label="Add locale"
                    >
                        +
                    </button>
                </div>
            )}

            <div className={styles.localeModeHint}>
                {isDefaultLocaleSelected
                    ? `Editing baseline locale ${getWidgetLocaleLabel(defaultLocale)}. This locale is always required and cannot be removed.`
                    : `Editing independent locale variant ${getWidgetLocaleLabel(selectedLocale)}.`}
            </div>
        </AppearanceTabSettingsContainer>
    );
};
